import { Matrix, Vector2, Vector3, Vector4 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';

/**
 * Class containing static functions to help procedurally build meshes
 */
export class TorusBuilder {
    /**
     * Creates a torus mesh
     * * The parameter `diameter` sets the diameter size (float) of the torus (default 1)
     * * The parameter `thickness` sets the diameter size of the tube of the torus (float, default 0.5)
     * * The parameter `tessellation` sets the number of torus sides (postive integer, default 16)
     * * You can also set the mesh side orientation with the values : BABYLON.Mesh.FRONTSIDE (default), BABYLON.Mesh.BACKSIDE or BABYLON.Mesh.DOUBLESIDE
     * * If you create a double-sided mesh, you can choose what parts of the texture image to crop and stick respectively on the front and the back sides with the parameters `frontUVs` and `backUVs` (Vector4). Detail here : https://doc.babylonjs.com/babylon101/discover_basic_elements#side-orientation
     * * The mesh can be set to updatable with the boolean parameter `updatable` (default false) if its internal geometry is supposed to change once created.
     * @param name defines the name of the mesh
     * @param options defines the options used to create the mesh
     * @param scene defines the hosting scene
     * @returns the torus mesh
     * @see https://doc.babylonjs.com/how_to/set_shapes#torus
     */
    public static CreateTorus(
        name: string,
        options: {
            diameter?: number;
            thickness?: number;
            tessellation?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
            arc?: number;
            slice?: number;
            updatable?: boolean;
            sliceStart?: number;
        },
        scene: any
    ): Mesh {
        const torus = new Mesh(name, scene);

        options.sideOrientation = Mesh._GetDefaultSideOrientation(options.sideOrientation);
        torus._originalBuilderSideOrientation = options.sideOrientation;

        const vertexData = createTorus(options);

        vertexData.applyToMesh(torus, options.updatable);

        return torus;

        function createTorus(options: {
            diameter?: number;
            thickness?: number;
            tessellation?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
            arc?: number;
            slice?: number;
            sliceStart?: number;
        }) {
            const indices = [];
            const positions = [];
            const normals = [];
            const uvs = [];

            const diameter = options.diameter || 1;
            const thickness = options.thickness || 0.5;
            const tessellation = options.tessellation || 12;
            const arc = options.arc && (options.arc <= 0 || options.arc > 1) ? 1.0 : options.arc || 1.0;
            let slice = options.slice && options.slice <= 0 ? 1.0 : options.slice || 1.0;
            const sliceStart =
                options.sliceStart && (options.sliceStart < 0 || options.sliceStart > 1) ? 0 : options.sliceStart || 0;
            const sideOrientation =
                options.sideOrientation === 0 ? 0 : options.sideOrientation || VertexData.DEFAULTSIDE;

            if (slice + sliceStart > 1) {
                slice = 1 - sliceStart;
            }

            const outerSegments = Math.ceil(tessellation * arc * Math.PI); // Need more segments for the torus circumference
            const innerSegments = Math.ceil(tessellation * slice);
            const closedRing = arc === 1; // handle float errors
            const closedTube = slice === 1;
            const lastOuterRing = outerSegments + 1;
            const lastTubeSection = innerSegments + 1;

            for (let i = 0; i < lastOuterRing; i++) {
                const u = i / outerSegments;

                const outerAngle = u * (arc * Math.PI * 2) - Math.PI / 2;

                const transform = Matrix.Translation(diameter / 2, 0, 0).multiply(Matrix.RotationY(outerAngle));

                for (let j = 0; j < lastTubeSection; j++) {
                    const v = 1 - j / innerSegments;

                    const innerAngle = (j / innerSegments) * (Math.PI * 2 * slice) + Math.PI * 2 * sliceStart;
                    const dx = Math.cos(innerAngle);
                    const dy = Math.sin(innerAngle);

                    // Create a vertex.
                    let normal = new Vector3(dx, dy, 0);
                    let position = normal.scale(thickness / 2);
                    const textureCoordinate = new Vector2(u, v);

                    position = Vector3.TransformCoordinates(position, transform);
                    normal = Vector3.TransformNormal(normal, transform);

                    positions.push(position.x, position.y, position.z);
                    normals.push(normal.x, normal.y, normal.z);
                    uvs.push(textureCoordinate.x, textureCoordinate.y);

                    if ((i < outerSegments || closedRing) && (j < innerSegments || closedTube)) {
                        // And create indices for two triangles.
                        const nextI = (i + 1) % lastOuterRing;
                        const nextJ = (j + 1) % lastTubeSection;

                        indices.push(i * lastTubeSection + j); // A
                        indices.push(i * lastTubeSection + nextJ); // B
                        indices.push(nextI * lastTubeSection + j); // C

                        indices.push(i * lastTubeSection + nextJ); // B
                        indices.push(nextI * lastTubeSection + nextJ); // D
                        indices.push(nextI * lastTubeSection + j); // C
                    }
                }
            }

            // Sides
            VertexData._ComputeSides(
                sideOrientation,
                positions,
                indices,
                normals,
                uvs,
                options.frontUVs,
                options.backUVs
            );

            // Result
            const vertexData = new VertexData();

            vertexData.indices = indices;
            vertexData.positions = positions;
            vertexData.normals = normals;
            vertexData.uvs = uvs;

            return vertexData;
        }
    }
}
